import React from 'react'
import { observer } from 'mobx-react-lite'
import Button from 'shared/ui/Button/Button'
import Modal from 'shared/ui/Modal/Modal'
import NoteCard from './components/NoteCard'
import useStudentNotesTab from './hooks/useStudentNotesTab'
import { getStudentFirstName } from './utils/formatSessionNoteMeta'
import s from './styles/StudentNotesTab.module.scss'

const StudentNotesTab = observer(
	({ studentId = null, studentName = 'Student', isActive = false }) => {
		const firstName = getStudentFirstName(studentName)
		const {
			listState,
			deleteTarget,
			isDeleting,
			handleOpenNote,
			handleRequestDelete,
			handleCancelDelete,
			handleConfirmDelete,
			handleStartRecording,
			handleRetry,
		} = useStudentNotesTab({ studentId, isActive })

		const { status, notes, total, error } = listState
		const isLoading = status === 'loading' || status === 'idle'
		const isEmpty = status === 'ready' && notes.length === 0

		return (
			<section className={s.wrap}>
				<header className={s.header}>
					<div className={s.headerText}>
						<div className={s.titleRow}>
							<h2 className={s.title}>Session Notes</h2>
							{status === 'ready' ? (
								<span className={s.badge}>{total}</span>
							) : null}
						</div>
						<p className={s.subtitle}>
							Notes from your sessions and meetings with {firstName}.
						</p>
					</div>

					<Button
						variant="primary"
						size="sm"
						onClick={handleStartRecording}
						disabled={!studentId}
					>
						+ Add Note
					</Button>
				</header>

				{isLoading ? (
					<div className={s.skeletonGrid} aria-busy="true" aria-label="Loading session notes">
						{Array.from({ length: 3 }).map((_, index) => (
							<div key={index} className={s.skeletonCard} />
						))}
					</div>
				) : null}

				{status === 'error' ? (
					<div className={s.state}>
						<p>{error?.message || 'Could not load session notes.'}</p>
						<Button variant="secondary" size="sm" onClick={handleRetry}>
							Retry
						</Button>
					</div>
				) : null}

				{isEmpty ? (
					<div className={s.state}>
						<p className={s.emptyTitle}>No session notes yet</p>
						<p className={s.emptyCopy}>
							Start a session to capture notes, summaries, and action items for{' '}
							{firstName}.
						</p>
						<Button variant="primary" size="sm" onClick={handleStartRecording}>
							+ Add Note
						</Button>
					</div>
				) : null}

				{status === 'ready' && notes.length > 0 ? (
					<div className={s.grid}>
						{notes.map(note => (
							<NoteCard
								key={note.id}
								note={note}
								onOpen={handleOpenNote}
								onDelete={handleRequestDelete}
							/>
						))}
					</div>
				) : null}

				<Modal
					isOpen={Boolean(deleteTarget)}
					onClose={handleCancelDelete}
					title="Delete session note?"
					subtitle="This will permanently remove the note, summary, and action items."
					ariaLabel="Delete session note confirmation"
					width={520}
					footerButtons={[
						{
							id: 'cancel',
							text: 'Cancel',
							variant: 'secondary',
							disabled: isDeleting,
							onClick: handleCancelDelete,
						},
						{
							id: 'delete',
							text: isDeleting ? 'Deleting...' : 'Delete',
							variant: 'primary',
							disabled: isDeleting,
							onClick: handleConfirmDelete,
						},
					]}
				>
					<p className={s.deleteCopy}>
						Are you sure you want to delete{' '}
						<strong>{deleteTarget?.title?.trim() || 'this session note'}</strong>?
					</p>
				</Modal>
			</section>
		)
	},
)

export default StudentNotesTab
